import {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import type { FC } from "react";

import {
    GetCourseDetails,
    LearningCourse
} from "../../handler/coursehandler";

const packageJson = require("../../../package.json");

interface InnerBannerProps {
    courseId: number;
}

interface CourseVideo {
    courseVideoId: number;
    courseVideoName: string;
    courseServerName: string;
    totalVideoDurationInSeconds: number;
    currentVideoDurationInSeconds: number;
    courseId: number;
    videoPercentage: number;
}

interface CourseLearningData {
    courseId: number;
    courseName: string;
    serverName: string;
    courseDescription: string;
    packageId: number;
    coursePrice: number;
    percentage: number;
    currentVideoId: number;
    courseVideos: CourseVideo[];
}

const emptyCourseVideo: CourseVideo = {
    courseVideoId: 0,
    courseVideoName: "",
    courseServerName: "",
    totalVideoDurationInSeconds: 0,
    currentVideoDurationInSeconds: 0,
    courseId: 0,
    videoPercentage: 0
};

const emptyCourse: CourseLearningData = {
    courseId: 0,
    courseName: "",
    serverName: "",
    courseDescription: "",
    packageId: 0,
    coursePrice: 0,
    percentage: 0,
    currentVideoId: 0,
    courseVideos: []
};

const Learning: FC<InnerBannerProps> = ({ courseId }) => {
    const playerRef = useRef<HTMLVideoElement | null>(null);
    const loadingCourse = useRef(false);
    const lastSavedSecond = useRef(-1);

    const [videoShow, setVideoShow] = useState("");
    const [videoLoading, setVideoLoading] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [isVideoPlay, setIsVideoPlay] = useState(false);
    const [userInformationId, setUserInformationId] = useState(0);
    const [selectedCourse, setSelectedCourse] =
        useState<CourseVideo>(emptyCourseVideo);
    const [courseLearning, setCourseLearning] =
        useState<CourseLearningData>(emptyCourse);

    /**
     * apiUrl should normally be:
     * https://api.growniverse.com/api
     *
     * The video endpoint is deliberately /videos/stream.
     * Do not use /courses because that route is already used by
     * the course-details endpoint and causes the EF nullable error.
     */
    const buildVideoUrl = useCallback((
        serverName: string,
        courseServerName: string
    ) => {
        const apiUrl = String(packageJson.apiUrl || "")
            .replace(/\/+$/, "");

        if (!apiUrl || !serverName || !courseServerName) {
            return "";
        }

        return `${apiUrl}/videos/stream` +
            `?courseName=${encodeURIComponent(serverName)}` +
            `&courseVideoName=${encodeURIComponent(courseServerName)}`;
    }, []);

    const loadVideo = useCallback((
        serverName: string,
        courseServerName: string,
        resumeAtSeconds = 0
    ) => {
        const url = buildVideoUrl(serverName, courseServerName);

        if (!url) {
            console.error("Invalid video information", {
                serverName,
                courseServerName
            });
            setVideoShow("");
            setVideoError(true);
            setVideoLoading(false);
            return;
        }

        console.log("Video streaming URL:", url);

        setVideoError(false);
        setVideoLoading(true);
        setIsVideoPlay(false);
        setVideoShow(url);

        // Store the resume position for the metadata event.
        // The video element is remounted after videoShow changes.
        window.setTimeout(() => {
            const video = playerRef.current;
            if (!video || resumeAtSeconds <= 0) return;

            const applyPosition = () => {
                if (resumeAtSeconds < video.duration) {
                    video.currentTime = resumeAtSeconds;
                }
            };

            if (video.readyState >= 1) {
                applyPosition();
            } else {
                video.addEventListener("loadedmetadata", applyPosition, {
                    once: true
                });
            }
        }, 0);
    }, [buildVideoUrl]);

    const saveVideoProgress = useCallback(async () => {
        const video = playerRef.current;

        if (!userInformationId ||
            !selectedCourse.courseVideoId ||
            !video) {
            return;
        }

        await LearningCourse({
            userInformationId,
            courseId,
            courseVideoId: selectedCourse.courseVideoId,
            videoTiming: video.currentTime
        });
    }, [courseId, selectedCourse.courseVideoId, userInformationId]);

    const refreshCourse = useCallback(async () => {
        const response = await GetCourseDetails(courseId);

        if (response?.statusCode !== 200 ||
            !response?.data?.courses) {
            return;
        }

        const course = response.data.courses as CourseLearningData;
        setCourseLearning(course);

        const updatedVideo = course.courseVideos?.find(
            (video: CourseVideo) =>
                video.courseVideoId === selectedCourse.courseVideoId
        );

        if (updatedVideo) {
            setSelectedCourse(updatedVideo);
        }
    }, [courseId, selectedCourse.courseVideoId]);

    const saveAndRefresh = useCallback(async () => {
        try {
            await saveVideoProgress();
            await refreshCourse();
        } catch (error) {
            console.error("Error saving course progress:", error);
        }
    }, [refreshCourse, saveVideoProgress]);

    const getCourseDetails = useCallback(async (
        currentUserInformationId: number
    ) => {
        if (loadingCourse.current) return;
        loadingCourse.current = true;

        try {
            const response = await GetCourseDetails(courseId);

            if (response?.statusCode !== 200 ||
                !response?.data?.courses) {
                console.error("Unable to get course details:", response);
                return;
            }

            const course = response.data.courses as CourseLearningData;
            setCourseLearning(course);

            if (!course.courseVideos?.length) {
                console.warn("No course videos found.");
                return;
            }

            const currentVideo =
                course.courseVideos.find(
                    (video: CourseVideo) =>
                        video.courseVideoId === course.currentVideoId
                ) || course.courseVideos[0];

            setSelectedCourse(currentVideo);
            lastSavedSecond.current = -1;

            // Direct Hostinger VPS endpoint. No S3, Wasabi, or private key.
            loadVideo(
                course.serverName,
                currentVideo.courseServerName,
                Number(currentVideo.currentVideoDurationInSeconds || 0)
            );

            await LearningCourse({
                userInformationId: currentUserInformationId,
                courseId,
                courseVideoId: currentVideo.courseVideoId,
                videoTiming: currentVideo.currentVideoDurationInSeconds
            });
        } catch (error) {
            console.error("Error loading course details:", error);
            setVideoError(true);
        } finally {
            loadingCourse.current = false;
        }
    }, [courseId, loadVideo]);

    const changeVideo = async (courseVideo: CourseVideo) => {
        if (selectedCourse.courseVideoId === courseVideo.courseVideoId) {
            const video = playerRef.current;
            if (!video) return;

            if (video.paused) {
                try {
                    await video.play();
                    setIsVideoPlay(true);
                } catch (error) {
                    console.error("Unable to play video:", error);
                }
            } else {
                video.pause();
                setIsVideoPlay(false);
            }
            return;
        }

        try {
            setSelectedCourse(courseVideo);
            setVideoShow("");
            setVideoError(false);
            setVideoLoading(true);
            setIsVideoPlay(false);
            lastSavedSecond.current = -1;

            await LearningCourse({
                userInformationId,
                courseId,
                courseVideoId: courseVideo.courseVideoId,
                videoTiming: courseVideo.currentVideoDurationInSeconds
            });

            loadVideo(
                courseLearning.serverName,
                courseVideo.courseServerName,
                Number(courseVideo.currentVideoDurationInSeconds || 0)
            );
        } catch (error) {
            console.error("Error changing video:", error);
            setVideoLoading(false);
            setVideoError(true);
        }
    };

    const handleTimeUpdate = () => {
        const video = playerRef.current;
        if (!video) return;

        const currentTime = Math.floor(video.currentTime);
        const totalTime = Math.floor(video.duration || 0);

        if (totalTime > 0 && currentTime >= totalTime - 1) {
            if (lastSavedSecond.current !== -2) {
                lastSavedSecond.current = -2;
                void saveAndRefresh();
            }
            return;
        }

        if (currentTime > 0 &&
            currentTime % 60 === 0 &&
            lastSavedSecond.current !== currentTime) {
            lastSavedSecond.current = currentTime;
            void saveAndRefresh();
        }
    };

    useEffect(() => {
        const storedProfile = localStorage.getItem("userProfile");
        if (!storedProfile) return;

        try {
            const userProfile = JSON.parse(storedProfile);
            const id = Number(userProfile?.userInformationId ?? 0);

            if (!id) {
                console.error("Invalid userInformationId");
                return;
            }

            setUserInformationId(id);
            window.scrollTo(0, 0);
            void getCourseDetails(id);
        } catch (error) {
            console.error("Error loading user profile:", error);
        }
    }, [getCourseDetails]);

    return (
        <section
            style={{
                width: "100%",
                minHeight: "100vh",
                background: "#050505",
                color: "#fff"
            }}
        >
            <div
                style={{
                    width: "100%",
                    background: "radial-gradient(circle at top, #161616 0%, #050505 55%, #000 100%)",
                    padding: "25px 15px 40px",
                    boxSizing: "border-box"
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: "1400px",
                        margin: "0 auto"
                    }}
                >
                    <div style={{ marginBottom: "20px" }}>
                        <h2
                            style={{
                                margin: 0,
                                fontSize: "26px",
                                fontWeight: 700,
                                color: "#fff"
                            }}
                        >
                            {courseLearning.courseName || "Course Learning"}
                        </h2>

                        {courseLearning.courseDescription && (
                            <p
                                style={{
                                    margin: "7px 0 0",
                                    color: "#999",
                                    fontSize: "14px"
                                }}
                            >
                                {courseLearning.courseDescription}
                            </p>
                        )}
                    </div>

                    <div
                        style={{
                            width: "100%",
                            background: "#000",
                            borderRadius: "16px",
                            overflow: "hidden",
                            border: "1px solid #222",
                            boxShadow: "0 15px 40px rgba(0,0,0,0.5)"
                        }}
                    >
                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                minHeight: videoShow ? undefined : "300px",
                                background: "#000"
                            }}
                        >
                            {videoLoading && videoShow && (
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "rgba(0,0,0,0.65)",
                                        color: "#f5b45f",
                                        fontSize: "14px"
                                    }}
                                >
                                    Loading video...
                                </div>
                            )}

                            {videoError && videoShow && (
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                        gap: "10px",
                                        background: "#000",
                                        color: "#aaa",
                                        fontSize: "14px",
                                        textAlign: "center",
                                        padding: "20px",
                                        boxSizing: "border-box"
                                    }}
                                >
                                    <strong style={{ color: "#fff" }}>
                                        Unable to load video
                                    </strong>
                                    <span>
                                        Please select this chapter again.
                                    </span>
                                </div>
                            )}

                            {videoShow && (
                                <video
                                    key={videoShow}
                                    ref={playerRef}
                                    controls
                                    controlsList="nodownload"
                                    preload="metadata"
                                    onContextMenu={(event) => event.preventDefault()}
                                    onTimeUpdate={handleTimeUpdate}
                                    onPlay={() => {
                                        setIsVideoPlay(true);
                                        setVideoError(false);
                                    }}
                                    onPause={() => setIsVideoPlay(false)}
                                    onEnded={() => {
                                        setIsVideoPlay(false);
                                        void saveAndRefresh();
                                    }}
                                    onCanPlay={() => {
                                        setVideoLoading(false);
                                        setVideoError(false);
                                    }}
                                    onError={() => {
                                        setVideoLoading(false);
                                        setVideoError(true);
                                        console.error("Video request failed:", videoShow);
                                    }}
                                    style={{
                                        width: "100%",
                                        maxHeight: "75vh",
                                        display: "block",
                                        background: "#000"
                                    }}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            )}

                            {!videoShow && !videoError && (
                                <div
                                    style={{
                                        width: "100%",
                                        minHeight: "300px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#000",
                                        color: "#666"
                                    }}
                                >
                                    Select a chapter to start learning.
                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                padding: "18px 20px",
                                background: "#0c0c0c"
                            }}
                        >
                            <h3
                                style={{
                                    margin: "0 0 12px",
                                    fontSize: "19px",
                                    color: "#fff"
                                }}
                            >
                                {selectedCourse.courseVideoName || "Video Title"}
                            </h3>

                            <div
                                style={{
                                    display: "flex",
                                    gap: "25px",
                                    flexWrap: "wrap",
                                    color: "#aaa",
                                    fontSize: "13px"
                                }}
                            >
                                <span>
                                    <strong style={{ color: "#fff" }}>
                                        Total Course:
                                    </strong>{" "}
                                    {courseLearning.percentage || 0}%
                                </span>

                                <span>
                                    <strong style={{ color: "#fff" }}>
                                        Chapter:
                                    </strong>{" "}
                                    {selectedCourse.videoPercentage || 0}%
                                </span>

                                <span>
                                    <strong style={{ color: "#fff" }}>
                                        Videos:
                                    </strong>{" "}
                                    {courseLearning.courseVideos.length}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: "25px" }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "12px"
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: "18px",
                                    color: "#fff"
                                }}
                            >
                                Course Chapters
                            </h3>

                            <span style={{ fontSize: "12px", color: "#888" }}>
                                {courseLearning.courseVideos.length} Chapters
                            </span>
                        </div>

                        <div
                            style={{
                                maxHeight: "400px",
                                overflowY: "auto",
                                background: "#090909",
                                border: "1px solid #202020",
                                borderRadius: "14px",
                                padding: "8px"
                            }}
                        >
                            {courseLearning.courseVideos.map((course, index) => {
                                const isSelected =
                                    selectedCourse.courseVideoId === course.courseVideoId;

                                const progress = Math.min(
                                    Math.max(Number(course.videoPercentage || 0), 0),
                                    100
                                );

                                return (
                                    <button
                                        type="button"
                                        key={course.courseVideoId}
                                        onClick={() => void changeVideo(course)}
                                        style={{
                                            width: "100%",
                                            padding: "15px",
                                            marginBottom: index === courseLearning.courseVideos.length - 1 ? 0 : "7px",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            background: isSelected ? "#1d1d1d" : "#101010",
                                            border: isSelected ? "1px solid #f5b45f" : "1px solid #1c1c1c",
                                            borderRadius: "10px",
                                            color: "#fff"
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "13px"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "34px",
                                                    height: "34px",
                                                    minWidth: "34px",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    background: isSelected ? "#f5b45f" : "#222",
                                                    color: isSelected ? "#000" : "#aaa",
                                                    fontSize: "13px",
                                                    fontWeight: 700
                                                }}
                                            >
                                                {index + 1}
                                            </div>

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    style={{
                                                        color: isSelected ? "#fff" : "#ccc",
                                                        fontSize: "14px",
                                                        fontWeight: isSelected ? 700 : 500,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    {course.courseVideoName}
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop: "5px",
                                                        fontSize: "11px",
                                                        color: "#777"
                                                    }}
                                                >
                                                    Progress: {progress}%
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    width: "35px",
                                                    textAlign: "center",
                                                    fontSize: "17px"
                                                }}
                                            >
                                                {isSelected ? (isVideoPlay ? "⏸" : "▶") : "▶"}
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                width: "100%",
                                                height: "3px",
                                                background: "#242424",
                                                borderRadius: "10px",
                                                marginTop: "12px",
                                                overflow: "hidden"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: `${progress}%`,
                                                    height: "100%",
                                                    background: isSelected ? "#f5b45f" : "#555",
                                                    borderRadius: "10px"
                                                }}
                                            />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Learning;